import React, { useMemo, useState } from "react";
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock3,
} from "lucide-react";

import {
  useGetMyLeaveBalanceQuery,
  useGetMyLeavesQuery,
} from "../../store/apis/leave.api";

const LeaveSummaryCard: React.FC = () => {
  const [showLeaveBreakdown, setShowLeaveBreakdown] = useState(false);

  const {
    data: balanceData,
    isLoading: loadingBalance,
  } = useGetMyLeaveBalanceQuery();

  const {
    data: leavesData,
    isLoading: loadingLeaves,
  } = useGetMyLeavesQuery();

  const loading = loadingBalance || loadingLeaves;

  const totalRemainingLeaves = Object.values(
    balanceData?.balancesMap || {}
  ).reduce(
    (sum, value) => sum + Number(value || 0),
    0
  );

  const leaveTypes = balanceData?.types || [];

  const filteredLeaveTypes = useMemo(() => {
    const allowedLeaveCodes = [
      "earned",
      "sick",
      "compensatory_leave",
    ];

    const uniqueLeaveMap = new Map();

    leaveTypes.forEach((leaveType) => {
      if (
        allowedLeaveCodes.includes(leaveType.code) &&
        !uniqueLeaveMap.has(leaveType.code)
      ) {
        uniqueLeaveMap.set(leaveType.code, leaveType);
      }
    });

    return Array.from(uniqueLeaveMap.values());
  }, [leaveTypes]);

  const leaves = leavesData?.data || [];

  const approvedLeaves = leaves.filter(
    (leave) =>
      leave.status?.toLowerCase() === "approved"
  );

  const takenLeavesDays = approvedLeaves.reduce(
    (sum, leave) => sum + Number(leave.days || 0),
    0
  );

  const pendingLeaves = leaves.filter(
    (leave) =>
      leave.status?.toLowerCase() === "pending"
  );

  const pendingRequestsCount = pendingLeaves.length;

  const hasNoData =
    totalRemainingLeaves === 0 &&
    takenLeavesDays === 0 &&
    pendingRequestsCount === 0;

  const leaveStats = [
    {
      label: "Leaves Taken",
      value: takenLeavesDays,
      unit: "Days",
      icon: CheckCircle2,
      iconColor: "text-emerald-600",
      iconBg: "bg-emerald-50",
    },
    {
      label: "Pending Requests",
      value: pendingRequestsCount,
      unit: "Requests",
      icon: Clock3,
      iconColor: "text-amber-600",
      iconBg: "bg-amber-50",
    },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-soft border border-slate-100 p-4 sm:p-6 h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem]">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-soft border border-slate-100 p-4 sm:p-6 h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem] flex flex-col hover:shadow-soft-hover transition-all duration-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-primary-600" />

          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-0">
              Leave Summary
            </h3>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
        {hasNoData ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-slate-400" />
            </div>

            <p className="text-sm font-semibold text-slate-500">
              No leave data available
            </p>

            <p className="text-xs text-slate-400 mt-1">
              Your leave activity will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="rounded-2xl transition-all duration-200 hover:bg-slate-50">
              <button
                type="button"
                onClick={() =>
                  setShowLeaveBreakdown(!showLeaveBreakdown)
                }
                className="w-full flex items-center justify-between px-4 py-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-primary-600" />
                  </div>

                  <div className="min-w-0 text-left">
                    <p className="text-sm font-medium text-slate-500 mb-1">
                      Available Leaves
                    </p>

                    <div className="flex items-end gap-1">
                      <span className="text-2xl font-bold text-slate-900 tracking-tight leading-none">
                        {totalRemainingLeaves}
                      </span>

                      <span className="text-sm text-slate-400 font-medium mb-0.5">
                        Days
                      </span>
                    </div>
                  </div>
                </div>

                <ChevronDown
                  className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${showLeaveBreakdown ? "rotate-180" : ""
                    }`}
                />
              </button>

              {showLeaveBreakdown && (
                <div className="px-4 pb-4">
                  <div className="ml-[3.75rem] space-y-2 border-l border-slate-100 pl-4">
                    {filteredLeaveTypes.map((leaveType) => (
                      <div
                        key={leaveType.code}
                        className="flex items-center justify-between py-1.5"
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-700">
                            {leaveType.label}
                          </p>
                        </div>

                        <div className="flex items-end gap-1">
                          <span className="text-sm font-bold text-slate-900">
                            {leaveType.available}
                          </span>

                          <span className="text-xs text-slate-400 mb-0.5">
                            Days
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {leaveStats.map((stat) => (
              <div
                key={stat.label}
                className="group flex items-center justify-between rounded-2xl px-4 py-4 hover:bg-slate-50 transition-all duration-200"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className={`w-11 h-11 rounded-xl ${stat.iconBg} flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-105`}
                  >
                    <stat.icon
                      className={`w-5 h-5 ${stat.iconColor}`}
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-500 mb-1">
                      {stat.label}
                    </p>

                    <div className="flex items-end gap-1">
                      <span className="text-2xl font-bold text-slate-900 tracking-tight leading-none">
                        {stat.value}
                      </span>

                      <span className="text-sm text-slate-400 font-medium mb-0.5">
                        {stat.unit}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className={`w-2.5 h-2.5 rounded-full ${stat.iconBg.replace(
                    "bg-",
                    "bg-"
                  )}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveSummaryCard;